// Complete UI translations for menu view
// This file contains all hardcoded text that appears on the menu

export type LanguageCode = 'fr' | 'en' | 'es' | 'de' | 'it' | 'nl' | 'pt' | 'zh' | 'ja';

export interface LanguageInfo {
  code: LanguageCode;
  name: string;
  flag: string;
  nativeName: string;
}

export const AVAILABLE_LANGUAGES: LanguageInfo[] = [
  { code: 'fr', name: 'Français', flag: '🇫🇷', nativeName: 'Français' },
  { code: 'en', name: 'English', flag: '🇬🇧', nativeName: 'English' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
  { code: 'de', name: 'German', flag: '🇩🇪', nativeName: 'Deutsch' },
  { code: 'it', name: 'Italian', flag: '🇮🇹', nativeName: 'Italiano' },
  { code: 'nl', name: 'Dutch', flag: '🇳🇱', nativeName: 'Nederlands' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹', nativeName: 'Português' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳', nativeName: '中文' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵', nativeName: '日本語' },
];

interface UITranslations {
  allergens: string;
  outOfStock: string;
  happyHour: string;
  followUs: string;
  order: string;
  delivery: string;
  restaurateurCTA: string;
  shareMenu: string;
  linkCopied: string;
  menuOf: string;
  banner: string;
  ourMenu: string;
  discoverDishes: string;
  book: string;
  call: string;
  viewMenu: string;
  drinks: string;
  dishes: string;
  prices: string;
  seeMore: string;
  close: string;
  upcomingEvents: string;
  seeAll: string;
  today: string;
  tomorrow: string;
  featured: string;
  places: string;
  getReminderOnDay: string;
  reminderActivated: string;
  reminderConfirmation: string;
  youWillReceiveNews: string;
  dontMissAnything: string;
  exclusiveOffers: string;
  gifts: string;
  yourEmail: string;
  subscribe: string;
  unsubscribeAnytime: string;
  subscriptionSuccess: string;
  thanksForSubscribing: string;
  checkInbox: string;
  alreadySubscribed: string;
  invalidEmail: string;
  enterEmail: string;
  errorOccurred: string;
  loadingMore: string;
  itemsDisplayed: string;
  // Happy Hour translations
  happyHourActive: string;
  happyHourEndsIn: string;
  happyHourStartsIn: string;
  nextHappyHour: string;
  // Day names
  sunday: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
}

// UI translations for menu view
export const MENU_UI_TRANSLATIONS: Record<LanguageCode, UITranslations> = {
  fr: {
    allergens: 'Allergènes',
    outOfStock: 'Épuisé',
    happyHour: 'Happy Hour!',
    followUs: 'Suivez-nous',
    order: 'Commander',
    delivery: 'Livraison',
    restaurateurCTA: 'Restaurateur ? Faites un essai gratuit',
    shareMenu: 'Partager le menu',
    linkCopied: 'Lien copié dans le presse-papier!',
    menuOf: 'Menu de',
    banner: 'Bannière',
    ourMenu: 'Notre Carte',
    discoverDishes: 'Découvrez nos plats et boissons',
    book: 'Réserver',
    call: 'Appeler',
    viewMenu: 'Voir la carte',
    drinks: 'Boissons',
    dishes: 'Plats',
    prices: 'Tarifs',
    seeMore: 'Voir plus',
    close: 'Fermer',
    upcomingEvents: 'Événements à venir',
    seeAll: 'Voir tout',
    today: "Aujourd'hui",
    tomorrow: 'Demain',
    featured: 'À la une',
    places: 'places',
    getReminderOnDay: 'Recevoir un rappel le jour J',
    reminderActivated: 'Rappel activé !',
    reminderConfirmation: 'On vous envoie un email le jour J.',
    youWillReceiveNews: 'Vous recevrez aussi les actualités de',
    dontMissAnything: 'Ne ratez rien !',
    exclusiveOffers: 'Recevez nos offres exclusives, actualités et parfois des',
    gifts: 'cadeaux',
    yourEmail: 'Votre email',
    subscribe: "S'inscrire",
    unsubscribeAnytime: 'Désinscription possible à tout moment',
    subscriptionSuccess: 'Inscription réussie ! Vérifiez votre boîte mail 📧',
    thanksForSubscribing: 'Merci de votre inscription !',
    checkInbox: 'Vérifiez votre boîte mail pour découvrir ce qui vous attend.',
    alreadySubscribed: 'Vous êtes déjà inscrit(e) !',
    invalidEmail: 'Veuillez saisir un email valide',
    enterEmail: 'Veuillez saisir votre email',
    errorOccurred: "Erreur lors de l'inscription",
    loadingMore: 'Chargement de plus d\'éléments...',
    itemsDisplayed: 'éléments affichés',
    happyHourActive: '🍻 Happy Hour en cours',
    happyHourEndsIn: 'Se termine dans',
    happyHourStartsIn: '🕐 Happy Hour commence dans',
    nextHappyHour: '🍺 Prochain Happy Hour',
    sunday: 'Dim',
    monday: 'Lun',
    tuesday: 'Mar',
    wednesday: 'Mer',
    thursday: 'Jeu',
    friday: 'Ven',
    saturday: 'Sam',
  },
  en: {
    allergens: 'Allergens',
    outOfStock: 'Out of stock',
    happyHour: 'Happy Hour!',
    followUs: 'Follow us',
    order: 'Order',
    delivery: 'Delivery',
    restaurateurCTA: 'Restaurant owner? Start your free trial',
    shareMenu: 'Share menu',
    linkCopied: 'Link copied to clipboard!',
    menuOf: 'Menu of',
    banner: 'Banner',
    ourMenu: 'Our Menu',
    discoverDishes: 'Discover our dishes and drinks',
    book: 'Book',
    call: 'Call',
    viewMenu: 'View menu',
    drinks: 'Drinks',
    dishes: 'Dishes',
    prices: 'Prices',
    seeMore: 'See more',
    close: 'Close',
    upcomingEvents: 'Upcoming Events',
    seeAll: 'See all',
    today: 'Today',
    tomorrow: 'Tomorrow',
    featured: 'Featured',
    places: 'spots',
    getReminderOnDay: 'Get a reminder on the day',
    reminderActivated: 'Reminder set!',
    reminderConfirmation: "We'll email you on the day.",
    youWillReceiveNews: "You'll also receive news from",
    dontMissAnything: "Don't miss a thing!",
    exclusiveOffers: 'Receive our exclusive offers, news and sometimes',
    gifts: 'gifts',
    yourEmail: 'Your email',
    subscribe: 'Subscribe',
    unsubscribeAnytime: 'Unsubscribe anytime',
    subscriptionSuccess: 'Subscribed! Check your inbox 📧',
    thanksForSubscribing: 'Thanks for subscribing!',
    checkInbox: "Check your inbox to see what's coming.",
    alreadySubscribed: "You're already subscribed!",
    invalidEmail: 'Please enter a valid email',
    enterEmail: 'Please enter your email',
    errorOccurred: 'Error during subscription',
    loadingMore: 'Loading more items...',
    itemsDisplayed: 'items displayed',
    happyHourActive: '🍻 Happy Hour in progress',
    happyHourEndsIn: 'Ends in',
    happyHourStartsIn: '🕐 Happy Hour starts in',
    nextHappyHour: '🍺 Next Happy Hour',
    sunday: 'Sun',
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
  },
  es: {
    allergens: 'Alérgenos',
    outOfStock: 'Agotado',
    happyHour: '¡Happy Hour!',
    followUs: 'Síguenos',
    order: 'Pedir',
    delivery: 'Entrega',
    restaurateurCTA: '¿Restaurador? Prueba gratis',
    shareMenu: 'Compartir menú',
    linkCopied: '¡Enlace copiado!',
    menuOf: 'Menú de',
    banner: 'Banner',
    ourMenu: 'Nuestra Carta',
    discoverDishes: 'Descubre nuestros platos y bebidas',
    book: 'Reservar',
    call: 'Llamar',
    viewMenu: 'Ver carta',
    drinks: 'Bebidas',
    dishes: 'Platos',
    prices: 'Precios',
    seeMore: 'Ver más',
    close: 'Cerrar',
    upcomingEvents: 'Próximos eventos',
    seeAll: 'Ver todo',
    today: 'Hoy',
    tomorrow: 'Mañana',
    featured: 'Destacado',
    places: 'plazas',
    getReminderOnDay: 'Recibir recordatorio el día',
    reminderActivated: '¡Recordatorio activado!',
    reminderConfirmation: 'Te enviaremos un email el día.',
    youWillReceiveNews: 'También recibirás noticias de',
    dontMissAnything: '¡No te pierdas nada!',
    exclusiveOffers: 'Recibe nuestras ofertas exclusivas, noticias y a veces',
    gifts: 'regalos',
    yourEmail: 'Tu email',
    subscribe: 'Suscribirse',
    unsubscribeAnytime: 'Cancelar suscripción en cualquier momento',
    subscriptionSuccess: '¡Suscrito! Revisa tu bandeja 📧',
    thanksForSubscribing: '¡Gracias por suscribirte!',
    checkInbox: 'Revisa tu bandeja para ver lo que te espera.',
    alreadySubscribed: '¡Ya estás suscrito!',
    invalidEmail: 'Por favor ingresa un email válido',
    enterEmail: 'Por favor ingresa tu email',
    errorOccurred: 'Error durante la suscripción',
    loadingMore: 'Cargando más elementos...',
    itemsDisplayed: 'elementos mostrados',
    happyHourActive: '🍻 Happy Hour en curso',
    happyHourEndsIn: 'Termina en',
    happyHourStartsIn: '🕐 Happy Hour comienza en',
    nextHappyHour: '🍺 Próximo Happy Hour',
    sunday: 'Dom',
    monday: 'Lun',
    tuesday: 'Mar',
    wednesday: 'Mié',
    thursday: 'Jue',
    friday: 'Vie',
    saturday: 'Sáb',
  },
  de: {
    allergens: 'Allergene',
    outOfStock: 'Ausverkauft',
    happyHour: 'Happy Hour!',
    followUs: 'Folgen Sie uns',
    order: 'Bestellen',
    delivery: 'Lieferung',
    restaurateurCTA: 'Gastronom? Kostenlos testen',
    shareMenu: 'Menü teilen',
    linkCopied: 'Link kopiert!',
    menuOf: 'Speisekarte von',
    banner: 'Banner',
    ourMenu: 'Unsere Karte',
    discoverDishes: 'Entdecken Sie unsere Gerichte und Getränke',
    book: 'Reservieren',
    call: 'Anrufen',
    viewMenu: 'Karte ansehen',
    drinks: 'Getränke',
    dishes: 'Gerichte',
    prices: 'Preise',
    seeMore: 'Mehr sehen',
    close: 'Schließen',
    upcomingEvents: 'Kommende Veranstaltungen',
    seeAll: 'Alle anzeigen',
    today: 'Heute',
    tomorrow: 'Morgen',
    featured: 'Empfohlen',
    places: 'Plätze',
    getReminderOnDay: 'Erinnerung am Tag erhalten',
    reminderActivated: 'Erinnerung aktiviert!',
    reminderConfirmation: 'Wir senden Ihnen am Tag eine E-Mail.',
    youWillReceiveNews: 'Sie erhalten auch Neuigkeiten von',
    dontMissAnything: 'Verpassen Sie nichts!',
    exclusiveOffers: 'Erhalten Sie unsere exklusiven Angebote, Neuigkeiten und manchmal',
    gifts: 'Geschenke',
    yourEmail: 'Ihre E-Mail',
    subscribe: 'Abonnieren',
    unsubscribeAnytime: 'Jederzeit abbestellen',
    subscriptionSuccess: 'Abonniert! Prüfen Sie Ihren Posteingang 📧',
    thanksForSubscribing: 'Danke für Ihre Anmeldung!',
    checkInbox: 'Prüfen Sie Ihren Posteingang.',
    alreadySubscribed: 'Sie sind bereits angemeldet!',
    invalidEmail: 'Bitte geben Sie eine gültige E-Mail ein',
    enterEmail: 'Bitte geben Sie Ihre E-Mail ein',
    errorOccurred: 'Fehler bei der Anmeldung',
    loadingMore: 'Mehr laden...',
    itemsDisplayed: 'Elemente angezeigt',
    happyHourActive: '🍻 Happy Hour aktiv',
    happyHourEndsIn: 'Endet in',
    happyHourStartsIn: '🕐 Happy Hour beginnt in',
    nextHappyHour: '🍺 Nächste Happy Hour',
    sunday: 'So',
    monday: 'Mo',
    tuesday: 'Di',
    wednesday: 'Mi',
    thursday: 'Do',
    friday: 'Fr',
    saturday: 'Sa',
  },
  it: {
    allergens: 'Allergeni',
    outOfStock: 'Esaurito',
    happyHour: 'Happy Hour!',
    followUs: 'Seguici',
    order: 'Ordinare',
    delivery: 'Consegna',
    restaurateurCTA: 'Ristoratore? Prova gratuita',
    shareMenu: 'Condividi menu',
    linkCopied: 'Link copiato!',
    menuOf: 'Menu di',
    banner: 'Banner',
    ourMenu: 'Il Nostro Menu',
    discoverDishes: 'Scopri i nostri piatti e bevande',
    book: 'Prenota',
    call: 'Chiama',
    viewMenu: 'Vedi menu',
    drinks: 'Bevande',
    dishes: 'Piatti',
    prices: 'Prezzi',
    seeMore: 'Vedi altro',
    close: 'Chiudi',
    upcomingEvents: 'Prossimi eventi',
    seeAll: 'Vedi tutto',
    today: 'Oggi',
    tomorrow: 'Domani',
    featured: 'In evidenza',
    places: 'posti',
    getReminderOnDay: 'Ricevi promemoria il giorno',
    reminderActivated: 'Promemoria attivato!',
    reminderConfirmation: 'Ti invieremo una email il giorno.',
    youWillReceiveNews: 'Riceverai anche novità da',
    dontMissAnything: 'Non perderti nulla!',
    exclusiveOffers: 'Ricevi le nostre offerte esclusive, novità e a volte',
    gifts: 'regali',
    yourEmail: 'La tua email',
    subscribe: 'Iscriviti',
    unsubscribeAnytime: 'Cancellati quando vuoi',
    subscriptionSuccess: 'Iscritto! Controlla la tua posta 📧',
    thanksForSubscribing: 'Grazie per esserti iscritto!',
    checkInbox: 'Controlla la tua posta.',
    alreadySubscribed: 'Sei già iscritto!',
    invalidEmail: 'Inserisci una email valida',
    enterEmail: 'Inserisci la tua email',
    errorOccurred: "Errore durante l'iscrizione",
    loadingMore: 'Caricamento...',
    itemsDisplayed: 'elementi visualizzati',
    happyHourActive: '🍻 Happy Hour in corso',
    happyHourEndsIn: 'Termina tra',
    happyHourStartsIn: '🕐 Happy Hour inizia tra',
    nextHappyHour: '🍺 Prossimo Happy Hour',
    sunday: 'Dom',
    monday: 'Lun',
    tuesday: 'Mar',
    wednesday: 'Mer',
    thursday: 'Gio',
    friday: 'Ven',
    saturday: 'Sab',
  },
  nl: {
    allergens: 'Allergenen',
    outOfStock: 'Uitverkocht',
    happyHour: 'Happy Hour!',
    followUs: 'Volg ons',
    order: 'Bestellen',
    delivery: 'Bezorging',
    restaurateurCTA: 'Restaurateur? Gratis proberen',
    shareMenu: 'Menu delen',
    linkCopied: 'Link gekopieerd!',
    menuOf: 'Menu van',
    banner: 'Banner',
    ourMenu: 'Ons Menu',
    discoverDishes: 'Ontdek onze gerechten en dranken',
    book: 'Reserveren',
    call: 'Bellen',
    viewMenu: 'Bekijk menu',
    drinks: 'Dranken',
    dishes: 'Gerechten',
    prices: 'Prijzen',
    seeMore: 'Meer zien',
    close: 'Sluiten',
    upcomingEvents: 'Komende evenementen',
    seeAll: 'Alles bekijken',
    today: 'Vandaag',
    tomorrow: 'Morgen',
    featured: 'Uitgelicht',
    places: 'plaatsen',
    getReminderOnDay: 'Ontvang herinnering op de dag',
    reminderActivated: 'Herinnering ingesteld!',
    reminderConfirmation: 'We sturen je een email op de dag.',
    youWillReceiveNews: 'Je ontvangt ook nieuws van',
    dontMissAnything: 'Mis niets!',
    exclusiveOffers: 'Ontvang onze exclusieve aanbiedingen, nieuws en soms',
    gifts: 'cadeaus',
    yourEmail: 'Je email',
    subscribe: 'Abonneren',
    unsubscribeAnytime: 'Op elk moment afmelden',
    subscriptionSuccess: 'Geabonneerd! Check je inbox 📧',
    thanksForSubscribing: 'Bedankt voor je aanmelding!',
    checkInbox: 'Check je inbox.',
    alreadySubscribed: 'Je bent al geabonneerd!',
    invalidEmail: 'Voer een geldig email in',
    enterEmail: 'Voer je email in',
    errorOccurred: 'Fout bij aanmelding',
    loadingMore: 'Meer laden...',
    itemsDisplayed: 'items weergegeven',
    happyHourActive: '🍻 Happy Hour bezig',
    happyHourEndsIn: 'Eindigt over',
    happyHourStartsIn: '🕐 Happy Hour begint over',
    nextHappyHour: '🍺 Volgende Happy Hour',
    sunday: 'Zo',
    monday: 'Ma',
    tuesday: 'Di',
    wednesday: 'Wo',
    thursday: 'Do',
    friday: 'Vr',
    saturday: 'Za',
  },
  pt: {
    allergens: 'Alergénios',
    outOfStock: 'Esgotado',
    happyHour: 'Happy Hour!',
    followUs: 'Siga-nos',
    order: 'Pedir',
    delivery: 'Entrega',
    restaurateurCTA: 'Restaurador? Teste grátis',
    shareMenu: 'Partilhar menu',
    linkCopied: 'Link copiado!',
    menuOf: 'Menu de',
    banner: 'Banner',
    ourMenu: 'Nosso Menu',
    discoverDishes: 'Descubra nossos pratos e bebidas',
    book: 'Reservar',
    call: 'Ligar',
    viewMenu: 'Ver menu',
    drinks: 'Bebidas',
    dishes: 'Pratos',
    prices: 'Preços',
    seeMore: 'Ver mais',
    close: 'Fechar',
    upcomingEvents: 'Próximos eventos',
    seeAll: 'Ver tudo',
    today: 'Hoje',
    tomorrow: 'Amanhã',
    featured: 'Destaque',
    places: 'lugares',
    getReminderOnDay: 'Receber lembrete no dia',
    reminderActivated: 'Lembrete ativado!',
    reminderConfirmation: 'Enviaremos um email no dia.',
    youWillReceiveNews: 'Também receberá novidades de',
    dontMissAnything: 'Não perca nada!',
    exclusiveOffers: 'Receba nossas ofertas exclusivas, novidades e às vezes',
    gifts: 'presentes',
    yourEmail: 'Seu email',
    subscribe: 'Inscrever',
    unsubscribeAnytime: 'Cancelar a qualquer momento',
    subscriptionSuccess: 'Inscrito! Verifique seu email 📧',
    thanksForSubscribing: 'Obrigado pela inscrição!',
    checkInbox: 'Verifique seu email.',
    alreadySubscribed: 'Você já está inscrito!',
    invalidEmail: 'Por favor insira um email válido',
    enterEmail: 'Por favor insira seu email',
    errorOccurred: 'Erro durante a inscrição',
    loadingMore: 'Carregando mais...',
    itemsDisplayed: 'itens exibidos',
    happyHourActive: '🍻 Happy Hour em andamento',
    happyHourEndsIn: 'Termina em',
    happyHourStartsIn: '🕐 Happy Hour começa em',
    nextHappyHour: '🍺 Próximo Happy Hour',
    sunday: 'Dom',
    monday: 'Seg',
    tuesday: 'Ter',
    wednesday: 'Qua',
    thursday: 'Qui',
    friday: 'Sex',
    saturday: 'Sáb',
  },
  zh: {
    allergens: '过敏原',
    outOfStock: '售罄',
    happyHour: '欢乐时光！',
    followUs: '关注我们',
    order: '订购',
    delivery: '配送',
    restaurateurCTA: '餐厅老板？免费试用',
    shareMenu: '分享菜单',
    linkCopied: '链接已复制！',
    menuOf: '菜单',
    banner: '横幅',
    ourMenu: '我们的菜单',
    discoverDishes: '发现我们的菜肴和饮品',
    book: '预订',
    call: '致电',
    viewMenu: '查看菜单',
    drinks: '饮品',
    dishes: '菜肴',
    prices: '价格',
    seeMore: '查看更多',
    close: '关闭',
    upcomingEvents: '即将举行的活动',
    seeAll: '查看全部',
    today: '今天',
    tomorrow: '明天',
    featured: '精选',
    places: '位置',
    getReminderOnDay: '当天接收提醒',
    reminderActivated: '提醒已设置！',
    reminderConfirmation: '我们将在当天发送邮件。',
    youWillReceiveNews: '您还将收到来自的新闻',
    dontMissAnything: '不要错过！',
    exclusiveOffers: '接收我们的独家优惠、新闻和有时',
    gifts: '礼物',
    yourEmail: '您的邮箱',
    subscribe: '订阅',
    unsubscribeAnytime: '随时取消订阅',
    subscriptionSuccess: '订阅成功！查看您的邮箱 📧',
    thanksForSubscribing: '感谢您的订阅！',
    checkInbox: '查看您的邮箱。',
    alreadySubscribed: '您已订阅！',
    invalidEmail: '请输入有效的邮箱',
    enterEmail: '请输入您的邮箱',
    errorOccurred: '订阅时出错',
    loadingMore: '加载更多...',
    itemsDisplayed: '项已显示',
    happyHourActive: '🍻 欢乐时光进行中',
    happyHourEndsIn: '结束于',
    happyHourStartsIn: '🕐 欢乐时光开始于',
    nextHappyHour: '🍺 下一个欢乐时光',
    sunday: '周日',
    monday: '周一',
    tuesday: '周二',
    wednesday: '周三',
    thursday: '周四',
    friday: '周五',
    saturday: '周六',
  },
  ja: {
    allergens: 'アレルゲン',
    outOfStock: '売り切れ',
    happyHour: 'ハッピーアワー！',
    followUs: 'フォローする',
    order: '注文',
    delivery: '配達',
    restaurateurCTA: 'レストランオーナー？無料お試し',
    shareMenu: 'メニューを共有',
    linkCopied: 'リンクをコピーしました！',
    menuOf: 'メニュー',
    banner: 'バナー',
    ourMenu: 'メニュー',
    discoverDishes: '料理と飲み物をご覧ください',
    book: '予約',
    call: '電話',
    viewMenu: 'メニューを見る',
    drinks: '飲み物',
    dishes: '料理',
    prices: '価格',
    seeMore: 'もっと見る',
    close: '閉じる',
    upcomingEvents: '今後のイベント',
    seeAll: 'すべて見る',
    today: '今日',
    tomorrow: '明日',
    featured: '注目',
    places: '席',
    getReminderOnDay: '当日リマインダーを受け取る',
    reminderActivated: 'リマインダー設定完了！',
    reminderConfirmation: '当日にメールをお送りします。',
    youWillReceiveNews: 'からのニュースも受け取ります',
    dontMissAnything: 'お見逃しなく！',
    exclusiveOffers: '限定オファー、ニュース、時には',
    gifts: 'ギフト',
    yourEmail: 'メールアドレス',
    subscribe: '登録',
    unsubscribeAnytime: 'いつでも解除可能',
    subscriptionSuccess: '登録完了！メールをご確認ください 📧',
    thanksForSubscribing: 'ご登録ありがとうございます！',
    checkInbox: 'メールをご確認ください。',
    alreadySubscribed: '既に登録済みです！',
    invalidEmail: '有効なメールを入力してください',
    enterEmail: 'メールを入力してください',
    errorOccurred: '登録中にエラーが発生しました',
    loadingMore: '読み込み中...',
    itemsDisplayed: '件表示中',
    happyHourActive: '🍻 ハッピーアワー開催中',
    happyHourEndsIn: '終了まで',
    happyHourStartsIn: '🕐 ハッピーアワー開始まで',
    nextHappyHour: '🍺 次回のハッピーアワー',
    sunday: '日',
    monday: '月',
    tuesday: '火',
    wednesday: '水',
    thursday: '木',
    friday: '金',
    saturday: '土',
  },
};

// Establishment type translations
export const ESTABLISHMENT_TYPES: Record<LanguageCode, Record<string, string>> = {
  fr: {
    'restaurant_traditionnel': 'Restaurant traditionnel',
    'brasserie': 'Brasserie',
    'bistrot': 'Bistrot',
    'auberge': 'Auberge',
    'relais': 'Relais',
    'gastronomique': 'Restaurant gastronomique',
    'fine_dining': 'Fine dining',
    'pizzeria': 'Pizzeria',
    'creperie': 'Crêperie',
    'grill': 'Grill',
    'rôtisserie': 'Rôtisserie',
    'wok': 'Wok',
    'sushi': 'Sushi',
    'asiatique': 'Restaurant asiatique',
    'chinois': 'Restaurant chinois',
    'japonais': 'Restaurant japonais',
    'thai': 'Restaurant thaï',
    'indien': 'Restaurant indien',
    'italien': 'Restaurant italien',
    'mexicain': 'Restaurant mexicain',
    'libanais': 'Restaurant libanais',
    'grec': 'Restaurant grec',
    'turc': 'Restaurant turc',
    'marocain': 'Restaurant marocain',
    'fast_food': 'Fast-food',
    'snack': 'Snack',
    'sandwicherie': 'Sandwicherie',
    'kebab': 'Kebab',
    'burger': 'Burger',
    'tacos': 'Tacos',
    'food_truck': 'Food truck',
    'bar': 'Bar',
    'pub': 'Pub',
    'wine_bar': 'Bar à vin',
    'cocktail_bar': 'Bar à cocktails',
    'café': 'Café',
    'salon_de_thé': 'Salon de thé',
    'glacier': 'Glacier',
    'boulangerie': 'Boulangerie',
    'pâtisserie': 'Pâtisserie',
    'autre': 'Autre',
  },
  en: {
    'restaurant_traditionnel': 'Traditional Restaurant',
    'brasserie': 'Brasserie',
    'bistrot': 'Bistro',
    'auberge': 'Inn',
    'relais': 'Relay',
    'gastronomique': 'Gourmet Restaurant',
    'fine_dining': 'Fine Dining',
    'pizzeria': 'Pizzeria',
    'creperie': 'Creperie',
    'grill': 'Grill',
    'rôtisserie': 'Rotisserie',
    'wok': 'Wok',
    'sushi': 'Sushi',
    'asiatique': 'Asian Restaurant',
    'chinois': 'Chinese Restaurant',
    'japonais': 'Japanese Restaurant',
    'thai': 'Thai Restaurant',
    'indien': 'Indian Restaurant',
    'italien': 'Italian Restaurant',
    'mexicain': 'Mexican Restaurant',
    'libanais': 'Lebanese Restaurant',
    'grec': 'Greek Restaurant',
    'turc': 'Turkish Restaurant',
    'marocain': 'Moroccan Restaurant',
    'fast_food': 'Fast Food',
    'snack': 'Snack Bar',
    'sandwicherie': 'Sandwich Shop',
    'kebab': 'Kebab',
    'burger': 'Burger',
    'tacos': 'Tacos',
    'food_truck': 'Food Truck',
    'bar': 'Bar',
    'pub': 'Pub',
    'wine_bar': 'Wine Bar',
    'cocktail_bar': 'Cocktail Bar',
    'café': 'Café',
    'salon_de_thé': 'Tea Room',
    'glacier': 'Ice Cream Shop',
    'boulangerie': 'Bakery',
    'pâtisserie': 'Pastry Shop',
    'autre': 'Other',
  },
  es: {
    'restaurant_traditionnel': 'Restaurante tradicional',
    'brasserie': 'Brasserie',
    'bistrot': 'Bistró',
    'pub': 'Pub',
    'bar': 'Bar',
    'wine_bar': 'Bar de vinos',
    'cocktail_bar': 'Bar de cócteles',
    'café': 'Café',
    'pizzeria': 'Pizzería',
    'autre': 'Otro',
  },
  de: {
    'restaurant_traditionnel': 'Traditionelles Restaurant',
    'brasserie': 'Brasserie',
    'bistrot': 'Bistro',
    'pub': 'Pub',
    'bar': 'Bar',
    'wine_bar': 'Weinbar',
    'cocktail_bar': 'Cocktailbar',
    'café': 'Café',
    'pizzeria': 'Pizzeria',
    'autre': 'Andere',
  },
  it: {
    'restaurant_traditionnel': 'Ristorante tradizionale',
    'brasserie': 'Brasserie',
    'bistrot': 'Bistrot',
    'pub': 'Pub',
    'bar': 'Bar',
    'wine_bar': 'Wine Bar',
    'cocktail_bar': 'Cocktail Bar',
    'café': 'Caffè',
    'pizzeria': 'Pizzeria',
    'autre': 'Altro',
  },
  nl: {
    'restaurant_traditionnel': 'Traditioneel restaurant',
    'brasserie': 'Brasserie',
    'bistrot': 'Bistro',
    'pub': 'Pub',
    'bar': 'Bar',
    'wine_bar': 'Wijnbar',
    'cocktail_bar': 'Cocktailbar',
    'café': 'Café',
    'pizzeria': 'Pizzeria',
    'autre': 'Anders',
  },
  pt: {
    'restaurant_traditionnel': 'Restaurante tradicional',
    'brasserie': 'Brasserie',
    'bistrot': 'Bistrô',
    'pub': 'Pub',
    'bar': 'Bar',
    'wine_bar': 'Bar de vinhos',
    'cocktail_bar': 'Bar de coquetéis',
    'café': 'Café',
    'pizzeria': 'Pizzaria',
    'autre': 'Outro',
  },
  zh: {
    'restaurant_traditionnel': '传统餐厅',
    'brasserie': '啤酒馆',
    'bistrot': '小酒馆',
    'pub': '酒吧',
    'bar': '酒吧',
    'wine_bar': '红酒吧',
    'cocktail_bar': '鸡尾酒吧',
    'café': '咖啡馆',
    'pizzeria': '比萨店',
    'autre': '其他',
  },
  ja: {
    'restaurant_traditionnel': '伝統的なレストラン',
    'brasserie': 'ブラッスリー',
    'bistrot': 'ビストロ',
    'pub': 'パブ',
    'bar': 'バー',
    'wine_bar': 'ワインバー',
    'cocktail_bar': 'カクテルバー',
    'café': 'カフェ',
    'pizzeria': 'ピッツェリア',
    'autre': 'その他',
  },
};

// Helper to get translated establishment type
export function getTranslatedEstablishmentType(type: string, language: LanguageCode): string {
  const translations = ESTABLISHMENT_TYPES[language] || ESTABLISHMENT_TYPES['fr'];
  return translations[type] || ESTABLISHMENT_TYPES['fr'][type] || type;
}

// Helper function to get translated item content
export function getTranslatedItem(
  item: { 
    name: string; 
    description?: string | null; 
    translations?: unknown;
  },
  language: LanguageCode
): { name: string; description: string | null } {
  if (language === 'fr' || !item.translations) {
    return { name: item.name, description: item.description || null };
  }
  
  // Safely parse translations
  const translations = item.translations as Record<string, { name?: string; description?: string }> | null;
  if (!translations || typeof translations !== 'object') {
    return { name: item.name, description: item.description || null };
  }
  
  const translation = translations[language];
  return {
    name: translation?.name || item.name,
    description: translation?.description || item.description || null,
  };
}

// Hook-like helper to get translation function
export function getT(language: LanguageCode) {
  return (key: keyof UITranslations): string => {
    return MENU_UI_TRANSLATIONS[language][key] || MENU_UI_TRANSLATIONS['fr'][key];
  };
}
